import type { FC } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import type { Theme } from '@mui/material';
import { useTranslation } from 'react-i18next';

import type { RevenueEntry } from '../types';
import RevenueDetailRow from './RevenueDetailRow';
import useStyles from './RevenueDetailTable.styles';

const GROUP_RANK: Record<string, number> = { base: 1, derived: 2, executive: 3 };

/** Deterministic sort: group order first, then description (case-insensitive), fallback to code */
const compareEntries = (a: RevenueEntry, b: RevenueEntry): number => {
  const gDiff = (GROUP_RANK[a.group] ?? 99) - (GROUP_RANK[b.group] ?? 99);
  if (gDiff !== 0) return gDiff;
  const cmp = a.label.localeCompare(b.label, undefined, { sensitivity: 'base' });
  if (cmp !== 0) return cmp;
  if (a.code < b.code) return -1;
  if (a.code > b.code) return 1;
  return 0;
};

const useHeaderStyles = makeStyles((theme: Theme) => ({
  tableHeader: {
    minWidth: 0,
    padding: theme.spacing(3, 3, 2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2, 1.5, 1.5),
    },
  },
  title: {
    color: '#000000',
    fontWeight: 500,
    overflowWrap: 'anywhere' as const,
  },
  subtitle: {
    marginTop: theme.spacing(0.5),
    color: '#000000',
    overflowWrap: 'anywhere' as const,
  },
  caption: {
    captionSide: 'top' as const,
    padding: theme.spacing(2, 3),
    color: '#000000',
    textAlign: 'left' as const,
    overflowWrap: 'anywhere' as const,
  },
}));

type RevenueDetailTableProps = {
  readonly entries: readonly RevenueEntry[];
};

const RevenueDetailTable: FC<RevenueDetailTableProps> = ({ entries }) => {
  const classes = useStyles();
  const headerClasses = useHeaderStyles();
  const { t } = useTranslation('revenue');

  const sorted = [...entries].sort(compareEntries);

  return (
    <Box className={classes.container}>
      <Paper elevation={1} className={classes.paper}>
        <Box component="header" className={headerClasses.tableHeader}>
          <Typography component="h2" variant="h6" className={headerClasses.title}>
            {t('table.title', 'Revenue detail')}
          </Typography>
          <Typography variant="body2" className={headerClasses.subtitle}>
            {t('table.subtitle', 'All 23 revenue accounting entries — auditable reference')}
          </Typography>
        </Box>

        <Box className={classes.tableWrapper}>
          <Table
            className={classes.table}
            size="small"
            aria-label={String(t('table.ariaLabel', 'Title company revenue metrics'))}
          >
            <caption className={headerClasses.caption}>
              {t('table.caption', 'Title Company Revenue Metrics')}
            </caption>
            <TableHead className={classes.tableHead}>
              <TableRow className={classes.headerRow}>
                <TableCell component="th" scope="col" className={classes.headerCell}>
                  {t('table.col.group', 'Group')}
                </TableCell>
                <TableCell component="th" scope="col" className={classes.headerCell}>
                  {t('table.col.description', 'Description')}
                </TableCell>
                <TableCell component="th" scope="col" className={classes.headerCell} align="right">
                  {t('table.col.amount', 'Amount')}
                </TableCell>
                <TableCell component="th" scope="col" className={classes.headerCell}>
                  {t('table.col.context', 'Context')}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody className={classes.tableBody}>
              {sorted.map((entry) => (
                <RevenueDetailRow key={entry.code} entry={entry} />
              ))}
            </TableBody>
          </Table>
        </Box>
      </Paper>
    </Box>
  );
};

export { RevenueDetailTable };
export default RevenueDetailTable;
